import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { WarningIcon } from '@common/components/icons';
import SavedReportsListModal from '../SavedReportsListModal/SavedReportsListModal';
import ScheduledReportsListModal from '../ScheduledReportsListModal/ScheduledReportsListModal';
import * as styles from './ReportDependencyWarning.module.css';

/**
 * Warning banner shown to report designers when editing a report
 * that has associated saved reports or scheduled reports.
 *
 * Displays clickable counts that open read-only list modals.
 * Uses the existing modal components with readOnly mode for consistent styling.
 */
function ReportDependencyWarning({
  savedReportsCount,
  scheduledReportsCount,
  savedReports,
  scheduledReports,
}) {
  const { t } = useTranslation();
  const [showSavedReportsModal, setShowSavedReportsModal] = useState(false);
  const [showScheduledReportsModal, setShowScheduledReportsModal] = useState(false);

  const handleSavedReportsClick = useCallback(() => {
    if (savedReportsCount > 0) {
      setShowSavedReportsModal(true);
    }
  }, [savedReportsCount]);

  const handleScheduledReportsClick = useCallback(() => {
    if (scheduledReportsCount > 0) {
      setShowScheduledReportsModal(true);
    }
  }, [scheduledReportsCount]);

  const handleCloseSavedReportsModal = useCallback(() => {
    setShowSavedReportsModal(false);
  }, []);

  const handleCloseScheduledReportsModal = useCallback(() => {
    setShowScheduledReportsModal(false);
  }, []);

  // Don't render if no dependencies
  if (savedReportsCount === 0 && scheduledReportsCount === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.warningBanner} role="alert">
        <div className={styles.warningIcon}>
          <WarningIcon size={20} />
        </div>
        <div className={styles.warningContent}>
          <p className={styles.warningMessage}>
            {t('reports.dependency.warningMessage')}
          </p>
          <div className={styles.dependencyCounts}>
            {savedReportsCount > 0 && (
              <button
                type="button"
                className={styles.countLink}
                onClick={handleSavedReportsClick}
              >
                {t('reports.dependency.savedReportsCount', { count: savedReportsCount })}
              </button>
            )}
            {savedReportsCount > 0 && scheduledReportsCount > 0 && (
              <span className={styles.separator}>•</span>
            )}
            {scheduledReportsCount > 0 && (
              <button
                type="button"
                className={styles.countLink}
                onClick={handleScheduledReportsClick}
              >
                {t('reports.dependency.scheduledReportsCount', { count: scheduledReportsCount })}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Saved Reports Modal (read-only mode) */}
      <SavedReportsListModal
        isOpen={showSavedReportsModal}
        onClose={handleCloseSavedReportsModal}
        readOnly
        data={savedReports}
      />

      {/* Scheduled Reports Modal (read-only mode) */}
      <ScheduledReportsListModal
        isOpen={showScheduledReportsModal}
        onClose={handleCloseScheduledReportsModal}
        readOnly
        data={scheduledReports}
      />
    </>
  );
}

ReportDependencyWarning.propTypes = {
  savedReportsCount: PropTypes.number,
  scheduledReportsCount: PropTypes.number,
  savedReports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      lastRun: PropTypes.string,
      status: PropTypes.number.isRequired,
    }),
  ),
  scheduledReports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      frequency: PropTypes.string,
      nextRun: PropTypes.string,
      status: PropTypes.number.isRequired,
    }),
  ),
};

ReportDependencyWarning.defaultProps = {
  savedReportsCount: 0,
  scheduledReportsCount: 0,
  savedReports: [],
  scheduledReports: [],
};

export default ReportDependencyWarning;
